import React, { useCallback, useMemo } from 'react'
import { useTheme } from '@mui/material'
import {
  useGetTaskById,
  useGetTaskLinkedProcesses,
  useRealTimeTask,
  useTaskPdfDownloadUrl,
} from '@core/react-query/features/tasks/task'
import { localizedStrings } from '@core/strings'
import { Box, Flex, Text } from '@fundamentals'
import { NavigationAwareTabs } from '@elements'
import { Button, DropdownButton, IconButton } from '@common/material'
import { useModal } from '@common/GlobalModal'
import { TaskActivityTab } from '@modules/tasks/taskDetails/tabs/TaskActivityTab'
import { TaskDetailsSidebar } from '@modules/tasks/taskDetails/TaskDetailsSidebar'
import { TaskStepAction } from './components/TaskStepAction'
import { EditTaskModal } from './modals/EditTaskModal'
import { ReassignTaskModal } from './modals/ReassignTaskModal'
import { ReopenTaskModal } from './modals/ReopenTaskModal'
import { CancelTaskModal } from './modals/CancelTaskModal'
import {
  ArrowDropDown,
  CancelOutlined,
  Clear as ClearIcon,
  Download,
  Edit,
  PersonOutlined,
  UndoOutlined,
} from '@mui/icons-material'
import { TaskStatusChip } from '@modules/tasks/components/TaskStatusChip'
import { useToast } from '@core/toast'
import { LinkedDocumentsList } from '@elements/LinkingProcesses/LinkedDocumentsList'
import LinkIcon from '@mui/icons-material/Link'
import { Palette } from '@core/theme/color-palette-siteassist'
import useConstants from '@core/providers/useConstants'
import { TaskPropertiesCard } from './components/TaskPropertiesCard'
import { useProductAccess } from 'webapp/providers/authentication'
import Utils from '@core/utils/utils'

type TaskDetailsActionsType = {
  icon: React.ReactElement
  name: string
  dataTest?: string
  onClick: () => void
}

type TaskDetailsProps = {
  taskId: number
  isModal?: boolean
}

export const TaskDetails: React.FC<TaskDetailsProps> = ({
  taskId,
  isModal,
}) => {
  const theme = useTheme()
  const toast = useToast()
  const { showModal, hideModal } = useModal()
  const { isViewMode } = useProductAccess()
  const { constants } = useConstants()
  const navBarHeight = constants.nav_bar_height
  const bgColor = isModal ? Palette.Common.White : Palette.Grey[50]

  const { data: task, isFetching: isFetchingTask } = useGetTaskById({
    taskId,
  })
  const downloadMutation = useTaskPdfDownloadUrl()
  const { data: linkedProcesses } = useGetTaskLinkedProcesses(
    {
      taskId,
    },
    {
      onError: (e) => toast.error(e?.message),
    },
  )

  useRealTimeTask(taskId, {
    enabled: !isFetchingTask,
  })
  const { data } = useGetTaskLinkedProcesses(
    {
      taskId,
    },
    {
      onError: (e) => toast.error(e.message),
    },
  )

  const linkedPermits = data?.getLinkedPermits() || []
  const linkedChecklists = data?.getLinkedChecklists() || []
  const linkedRegisterItems = data?.getLinkedRegisterItems() || []
  const linkedInspections = data?.getLinkedInspections() || []
  const linkedObservations = data?.getLinkedObservations() || []

  const handleDownload = useCallback(async () => {
    const pdfDownloadUrl = await downloadMutation.mutateAsync({
      taskId,
    })

    const toastId = Utils.GUID()
    toast.progress(localizedStrings.downloadingTask, {
      duration: 80 * 1000,
      id: toastId,
    })

    const fileGenerated = await Utils.pollForSuccessfulRequest(
      pdfDownloadUrl.urlDownload,
    )

    if (!fileGenerated) {
      toast.error(localizedStrings.taskDownloadFailed, { id: toastId })
    } else {
      window.location.assign(pdfDownloadUrl.urlDownload)
      toast.success(localizedStrings.taskDownloadComplete, { id: toastId })
    }
  }, [taskId, downloadMutation, toast])

  const tabs = useMemo(() => {
    const tabBuilder = NavigationAwareTabs.TabBuilder().addTab({
      name: localizedStrings.activityTimeline,
      label: localizedStrings.activityTimeline,
      clearParams: true,
      component: (
        <TaskActivityTab task={task} bgColor={bgColor} isModal={isModal} />
      ),
    })

    if (linkedPermits.length) {
      tabBuilder.addTab({
        name: 'Permits',
        icon: <LinkIcon />,
        label: localizedStrings.permits,
        badgeCount: linkedPermits.length,
        component: (
          <LinkedDocumentsList
            items={linkedPermits}
            organisationId={task?.organisationId}
          />
        ),
      })
    }

    if (linkedChecklists.length) {
      tabBuilder.addTab({
        name: 'Checklists',
        icon: <LinkIcon />,
        label: localizedStrings.checklists,
        badgeCount: linkedChecklists.length,
        component: (
          <LinkedDocumentsList
            items={linkedChecklists}
            organisationId={task?.organisationId}
          />
        ),
      })
    }

    if (linkedRegisterItems.length) {
      tabBuilder.addTab({
        name: 'Register Items',
        icon: <LinkIcon />,
        label: localizedStrings.registerItems,
        badgeCount: linkedRegisterItems.length,
        component: (
          <LinkedDocumentsList
            items={linkedRegisterItems}
            organisationId={task?.organisationId}
          />
        ),
      })
    }

    if (linkedInspections.length) {
      tabBuilder.addTab({
        name: 'Inspections',
        icon: <LinkIcon />,
        label: localizedStrings.inspections,
        badgeCount: linkedInspections.length,
        component: (
          <LinkedDocumentsList
            items={linkedInspections}
            organisationId={task?.organisationId}
          />
        ),
      })
    }

    if (linkedObservations.length) {
      tabBuilder.addTab({
        name: 'Observations',
        icon: <LinkIcon />,
        label: localizedStrings.observations,
        badgeCount: linkedObservations.length,
        component: (
          <LinkedDocumentsList
            items={linkedObservations}
            organisationId={task?.organisationId}
          />
        ),
      })
    }

    return tabBuilder.build()
  }, [task, data])

  const secondaryActionOptionsMemoised = useMemo(() => {
    const secondaryActionOptions: TaskDetailsActionsType[] = []

    const editTaskAction: TaskDetailsActionsType = {
      icon: <Edit />,
      name: localizedStrings.edit,
      dataTest: 'edit-task',
      onClick: () => showModal(<EditTaskModal task={task} />),
    }

    const reassignTaskAction: TaskDetailsActionsType = {
      icon: <PersonOutlined />,
      name: 'Reassign',
      dataTest: 'reassign-task',
      onClick: () => showModal(<ReassignTaskModal task={task} />),
    }

    const cancelTaskAction: TaskDetailsActionsType = {
      icon: <CancelOutlined />,
      name: localizedStrings.cancel,
      dataTest: 'cancel-task',
      onClick: () => {
        showModal(<CancelTaskModal task={task} />)
      },
    }

    const reopenTaskAction: TaskDetailsActionsType = {
      icon: <UndoOutlined />,
      name: localizedStrings.reopen,
      dataTest: 'reopen-task',
      onClick: () => showModal(<ReopenTaskModal task={task} />),
    }

    const downloadTaskAction: TaskDetailsActionsType = {
      icon: <Download />,
      name: localizedStrings.downloadTask,
      dataTest: 'download-task',
      onClick: handleDownload,
    }

    secondaryActionOptions.push(downloadTaskAction)
    if (task?.hasPermission('EDIT')) {
      secondaryActionOptions.push(editTaskAction)
    }

    if (task?.hasPermission('REASSIGN')) {
      secondaryActionOptions.push(reassignTaskAction)
    }

    if (!task?.isCancelled() && task?.hasPermission('CANCEL')) {
      secondaryActionOptions.push(cancelTaskAction)
    }

    if (task?.hasPermission('REOPEN')) {
      secondaryActionOptions.push(reopenTaskAction)
    }

    return secondaryActionOptions
  }, [task, handleDownload])

  const refByChecklistLinkedProcess = useMemo(
    () =>
      linkedProcesses?.linkedProcessItemResults?.find(
        (linkedProcess) => linkedProcess.type === 'REFERENCED_BY_INSPECTION',
      ),
    [linkedProcesses],
  )

  const stickyPosition = {
    HEADER: isModal ? 0 : `calc(${navBarHeight}px + 16px)`,
    TAB_HEADER: isModal ? '120px' : '183px',
    ACTION_BUTTON: isModal ? '64px' : '127px',
  }

  if (!task) return null

  return (
    <Flex>
      <Flex flex={1} alignItems='flex-start' justifyContent='center'>
        <Box flex={1} maxWidth='960px'>
          <Box
            data-test='task-details'
            top={stickyPosition.HEADER}
            zIndex={10}
            position='sticky'
            bgcolor={bgColor}
            px='3'
            pb={2}
          >
            <Flex justifyContent='space-between' alignItems='center' mb={0.5}>
              <Flex flexDirection='column' mt={1.5}>
                <Text variant='body2' color='text.secondary' mb={0.5}>
                  {`${task?.template?.currentVersion?.templateName}`}
                </Text>
                <Text
                  variant='h5'
                  data-test='task-summary'
                >{`${task?.summary}`}</Text>
              </Flex>
            </Flex>

            <Flex flexDirection='row' gap={1} alignItems={'center'}>
              <Box>
                <TaskStatusChip
                  status={task.status}
                  label={task.currentStep?.name}
                  hasRequestedChanges={task.hasRequestedChanges()}
                />
              </Box>

              <Box flexGrow={1} />
            </Flex>
          </Box>
          <TaskPropertiesCard task={task} />
          <NavigationAwareTabs
            tabs={tabs}
            withoutQueryParam={isModal}
            hideNavigationOnSingleTab
            tabHeaderSx={{
              position: 'sticky',
              top: stickyPosition.TAB_HEADER,
              bgcolor: bgColor,
              zIndex: 20,
            }}
          />
        </Box>
      </Flex>

      <Box sx={{ pl: 3, width: '23rem' }}>
        {isModal && (
          <IconButton
            aria-label='close-modal'
            onClick={hideModal}
            data-test='close-modal'
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              mt: 2,
              mr: 2,
            }}
          >
            <ClearIcon />
          </IconButton>
        )}
        <Box
          position='sticky'
          top={stickyPosition.ACTION_BUTTON}
          maxHeight={`calc(100vh - ${stickyPosition.ACTION_BUTTON})`}
          bgcolor={bgColor}
          zIndex={20}
          display='flex'
          flexDirection='column'
        >
          <Flex pb={2} justifyContent='flex-end'>
            <TaskStepAction task={task} />
            {!isViewMode && secondaryActionOptionsMemoised.length > 0 && (
              <DropdownButton
                label={localizedStrings.actions}
                options={secondaryActionOptionsMemoised.map(
                  (secondaryOptions) => ({
                    title: secondaryOptions.name,
                    value: secondaryOptions.name,
                    'data-test': secondaryOptions.dataTest,
                    onSelect: secondaryOptions.onClick,
                    icon: secondaryOptions.icon,
                  }),
                )}
                renderButton={(onClick) => (
                  <Button
                    variant='outlined'
                    data-test='secondary-task-actions'
                    endIcon={<ArrowDropDown />}
                    onClick={onClick}
                    sx={{
                      fontSize: 16,
                      background: theme.palette.legacy.white[10],
                    }}
                  >
                    {localizedStrings.actions}
                  </Button>
                )}
              />
            )}
          </Flex>
          <Box overflow='auto'>
            <TaskDetailsSidebar
              task={task}
              checklist={refByChecklistLinkedProcess?.linkedInspectionResult}
              answer={refByChecklistLinkedProcess?.answer}
            />
          </Box>
        </Box>
      </Box>
    </Flex>
  )
}
